import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
`;

const HeaderLeft = styled.div``;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  font-size: 0.875rem;
  margin: 0;
`;

const ActionButton = styled.button<{ $variant?: string }>`
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$variant === 'primary'
    ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
    : 'rgba(255, 255, 255, 0.1)'};
  color: white;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled(motion.div)<{ $color: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${props => props.$color};
  }
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const StatSubtext = styled.div`
  color: #64748b;
  font-size: 0.7rem;
  margin-top: 0.25rem;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const Panel = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
`;

const PanelTitle = styled.h3`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExposureBar = styled.div`
  margin-bottom: 0.75rem;
`;

const ExposureBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const ExposureBarLabel = styled.span`
  color: #e2e8f0;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ExposureBarValue = styled.span`
  color: #94a3b8;
  font-size: 0.8rem;
`;

const ExposureBarTrack = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  overflow: hidden;
`;

const ExposureBarFill = styled.div<{ $width: number; $color: string; $warning?: boolean }>`
  height: 100%;
  width: ${props => props.$width}%;
  background: ${props => props.$warning ? '#ef4444' : props.$color};
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

const Th = styled.th`
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  color: #e2e8f0;
  font-size: 0.8rem;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const Badge = styled.span<{ $color: string }>`
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  border: 1px solid ${props => props.$color}40;
`;

const ConfidenceBar = styled.div<{ $value: number }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: ${props => props.$value >= 80 ? '#10b981' : props.$value >= 50 ? '#f59e0b' : '#ef4444'};
  font-weight: 600;
`;

const WarningBanner = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const WarningText = styled.div`
  color: #fca5a5;
  font-size: 0.8rem;
  line-height: 1.5;
`;

const FullWidthPanel = styled(Panel)`
  grid-column: 1 / -1;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${props => props.$active ? '#3b82f6' : 'rgba(255,255,255,0.1)'};
  background: ${props => props.$active ? 'rgba(59,130,246,0.2)' : 'transparent'};
  color: ${props => props.$active ? '#60a5fa' : '#94a3b8'};
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    color: #60a5fa;
  }
`;

const SECTOR_COLORS: Record<string, string> = {
  'Technology': '#3b82f6',
  'Healthcare': '#10b981',
  'Financials': '#8b5cf6',
  'Consumer': '#f59e0b',
  'Energy': '#ef4444',
  'Industrials': '#06b6d4',
  'Real Estate': '#ec4899',
  'Communications': '#84cc16',
  'Materials': '#f97316',
  'Utilities': '#a78bfa',
  'Unclassified': '#64748b',
};

interface SectorExposure {
  sector: string;
  taxonomyCode: string;
  marketValue: number;
  positionCount: number;
  percentage: number;
  topHoldings: string[];
}

interface ClassificationResult {
  securityId: number;
  securityName: string;
  rawSector: string;
  rawIndustry: string;
  taxonomyCode: string;
  standardizedSector: string;
  confidence: number;
  status: string;
}

interface ExposureData {
  totalMarketValue: number;
  totalPositions: number;
  sectorCount: number;
  herfindahlIndex: number;
  topSector: string;
  topSectorPercentage: number;
  warnings: string[];
  exposures: SectorExposure[];
}

interface ClassificationData {
  totalSecurities: number;
  highConfidence: number;
  needsReview: number;
  unclassified: number;
  sectorDistribution: Record<string, number>;
  classifications: ClassificationResult[];
}

const ExposureClassificationScreen: React.FC = () => {
  const [exposure, setExposure] = useState<ExposureData | null>(null);
  const [classifications, setClassifications] = useState<ClassificationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [classifyingAll, setClassifyingAll] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [expRes, classRes] = await Promise.all([
        axios.get('/api/credit/exposure/portfolio').catch(() => null),
        axios.get('/api/credit/exposure/classifications').catch(() => null),
      ]);

      if (expRes?.data) {
        setExposure(expRes.data);
      }

      if (classRes?.data) {
        setClassifications(classRes.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRunClassification = async () => {
    setClassifyingAll(true);
    try {
      // POST is the persisting run; the GET on page load is read-only.
      const res = await axios.post('/api/credit/exposure/classifications/run');
      setClassifications(res.data);
    } catch (err) {
      console.error('Failed to run classification:', err);
    } finally {
      setClassifyingAll(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const filteredClassifications = classifications?.classifications?.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'needs_review') return c.status === 'NEEDS_REVIEW';
    if (filter === 'unclassified') return c.status === 'UNCLASSIFIED';
    if (filter === 'high_confidence') return c.status === 'AI_ASSIGNED';
    return true;
  }) || [];

  if (loading) {
    return <Container><div style={{ color: '#94a3b8', textAlign: 'center', padding: '4rem' }}>Loading exposure data...</div></Container>;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>Exposure Classification</Title>
          <Subtitle>AI-powered standardized sector taxonomy with concentration monitoring</Subtitle>
        </HeaderLeft>
        <ActionButton $variant="primary" onClick={handleRunClassification} disabled={classifyingAll}>
          {classifyingAll ? 'Classifying...' : 'Run AI Classification'}
        </ActionButton>
      </Header>

      {/* Warnings */}
      {exposure?.warnings && exposure.warnings.length > 0 && (
        <WarningBanner>
          <span style={{ fontSize: '1.25rem' }}>&#9888;</span>
          <WarningText>
            <strong>Concentration Alerts:</strong>
            {exposure.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </WarningText>
        </WarningBanner>
      )}

      {/* Stats */}
      <StatsGrid>
        <StatCard $color="#3b82f6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatLabel>Total Positions</StatLabel>
          <StatValue>{exposure?.totalPositions || 0}</StatValue>
          <StatSubtext>{formatCurrency(exposure?.totalMarketValue || 0)} market value</StatSubtext>
        </StatCard>
        <StatCard $color="#10b981" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <StatLabel>AI Classified</StatLabel>
          <StatValue>{classifications?.highConfidence || 0}</StatValue>
          <StatSubtext>{classifications ? ((classifications.highConfidence / classifications.totalSecurities) * 100).toFixed(0) : 0}% high confidence</StatSubtext>
        </StatCard>
        <StatCard $color="#f59e0b" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatLabel>Needs Review</StatLabel>
          <StatValue>{classifications?.needsReview || 0}</StatValue>
          <StatSubtext>Low confidence or ambiguous</StatSubtext>
        </StatCard>
        <StatCard $color="#ef4444" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
          <StatLabel>Unclassified</StatLabel>
          <StatValue>{classifications?.unclassified || 0}</StatValue>
          <StatSubtext>Missing sector data</StatSubtext>
        </StatCard>
        <StatCard $color="#8b5cf6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatLabel>Distinct Sectors</StatLabel>
          <StatValue>{exposure?.sectorCount || 0}</StatValue>
          <StatSubtext>HHI: {exposure?.herfindahlIndex?.toFixed(0) || 'N/A'}</StatSubtext>
        </StatCard>
        <StatCard $color="#ec4899" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <StatLabel>Top Sector</StatLabel>
          <StatValue style={{ fontSize: '1.25rem' }}>{exposure?.topSector || 'N/A'}</StatValue>
          <StatSubtext>{exposure?.topSectorPercentage?.toFixed(1)}% of portfolio</StatSubtext>
        </StatCard>
      </StatsGrid>

      {/* Exposure Breakdown + Sector Distribution */}
      <ContentGrid>
        <Panel>
          <PanelTitle>Portfolio Exposure by Sector</PanelTitle>
          {exposure?.exposures?.map((exp, i) => (
            <ExposureBar key={i}>
              <ExposureBarHeader>
                <ExposureBarLabel>{exp.sector}</ExposureBarLabel>
                <ExposureBarValue>{exp.percentage.toFixed(1)}% &middot; {formatCurrency(exp.marketValue)} &middot; {exp.positionCount} pos</ExposureBarValue>
              </ExposureBarHeader>
              <ExposureBarTrack>
                <ExposureBarFill
                  $width={exp.percentage}
                  $color={SECTOR_COLORS[exp.sector] || '#64748b'}
                  $warning={exp.percentage > 25}
                />
              </ExposureBarTrack>
            </ExposureBar>
          ))}
        </Panel>

        <Panel>
          <PanelTitle>Classification Distribution</PanelTitle>
          {classifications?.sectorDistribution && Object.entries(classifications.sectorDistribution)
            .sort(([, a], [, b]) => b - a)
            .map(([sector, count], i) => (
              <ExposureBar key={i}>
                <ExposureBarHeader>
                  <ExposureBarLabel>{sector}</ExposureBarLabel>
                  <ExposureBarValue>{count} securities</ExposureBarValue>
                </ExposureBarHeader>
                <ExposureBarTrack>
                  <ExposureBarFill
                    $width={classifications ? (count / classifications.totalSecurities) * 100 : 0}
                    $color={SECTOR_COLORS[sector] || '#64748b'}
                  />
                </ExposureBarTrack>
              </ExposureBar>
            ))}
        </Panel>
      </ContentGrid>

      {/* Classification Detail Table */}
      <FullWidthPanel>
        <PanelTitle>Security Classifications</PanelTitle>
        <FilterBar>
          {[
            { key: 'all', label: `All (${classifications?.totalSecurities || 0})` },
            { key: 'high_confidence', label: `Classified (${classifications?.highConfidence || 0})` },
            { key: 'needs_review', label: `Needs Review (${classifications?.needsReview || 0})` },
            { key: 'unclassified', label: `Unclassified (${classifications?.unclassified || 0})` },
          ].map(f => (
            <FilterChip key={f.key} $active={filter === f.key} onClick={() => setFilter(f.key)}>
              {f.label}
            </FilterChip>
          ))}
        </FilterBar>
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <Th>Security</Th>
                <Th>Raw Sector</Th>
                <Th>Raw Industry</Th>
                <Th>Standardized Sector</Th>
                <Th>Taxonomy Code</Th>
                <Th>Confidence</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {filteredClassifications.map((c, i) => (
                <tr key={i}>
                  <Td style={{ fontWeight: 600 }}>{c.securityName}</Td>
                  <Td style={{ color: '#94a3b8' }}>{c.rawSector || '-'}</Td>
                  <Td style={{ color: '#94a3b8' }}>{c.rawIndustry || '-'}</Td>
                  <Td>
                    <span style={{ color: SECTOR_COLORS[c.standardizedSector] || '#94a3b8', fontWeight: 600 }}>
                      {c.standardizedSector}
                    </span>
                  </Td>
                  <Td><code style={{ color: '#94a3b8', fontSize: '0.7rem' }}>{c.taxonomyCode}</code></Td>
                  <Td>
                    <ConfidenceBar $value={c.confidence}>
                      {c.confidence.toFixed(0)}%
                    </ConfidenceBar>
                  </Td>
                  <Td>
                    <Badge $color={
                      c.status === 'AI_ASSIGNED' ? '#10b981' :
                      c.status === 'NEEDS_REVIEW' ? '#f59e0b' : '#ef4444'
                    }>
                      {c.status.replace('_', ' ')}
                    </Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </FullWidthPanel>
    </Container>
  );
};

export default ExposureClassificationScreen;
